import React, { useEffect, useRef, useState } from 'react'
import { SubscriptionData, useFreeShippingData } from 'hooks/subscriptions'
import { useStoreView } from 'hooks/useStoreView'
import { CustomizationWrapper, CustomizeWrapper, SubscriptionWrapper, Wrapper } from './components/Styled'
import { MagentoProductPrices } from 'lib/types'
import { QuantityValue } from 'scenes/Product/components/OptionsSection'
import { FlexWrapper } from 'lib/ui/components'
import { SubscriptionFrequencyInput } from 'components/SubscriptionFrequencyInput'
import { ProductQtyInput } from '../ProductQtyInput'
import { CustomizationItem } from './components/CustomizationItem'

interface Props {
  productId: number
  packageQty?: string
  frequency: number
  setFrequency: (x: number) => void
  productPrice: MagentoProductPrices
  packsToAdd: number
  setPacksToAdd: (x: number) => void
  subscriptionsData: SubscriptionData[]
  setQuantityPerDay: (param: QuantityValue) => void
  quantityPerDay: QuantityValue
  packageType?: string
  subtotalPrice: number
}

export const Subscriptions: React.FC<Props> = (
  {
    productId,
    packageQty,
    setFrequency,
    frequency,
    productPrice,
    packsToAdd,
    setPacksToAdd,
    subscriptionsData,
    quantityPerDay,
    setQuantityPerDay,
    packageType,
    subtotalPrice
  }) => {
  const { isLivBrite } = useStoreView()
  const customizationRef = useRef(null)

  useEffect(() => {
    if (frequency === 0) {
      setFrequency(subscriptionsData?.[0].itemFrequency)
    }
    // eslint-disable-next-line
  }, [frequency])

  const quantityUsedPerDay = Math.ceil(quantityPerDay.value)
  const [packs, setPacks] = useState({ value: packsToAdd })
  const { isFreeShipping, loadingSubscription } = useFreeShippingData(productId, (frequency * 7), packs.value)

  const itemsPerDay = getItemsPerDay({ packs: packs.value, packageQty: parseFloat(packageQty ?? ''), frequency })

  const handlePacksChange = (qty: any) => {
    const quantity = parseInt(qty.target.value)
    if (quantity > 0 && quantity < 9999) {
      setPacks({ value: quantity })
      setPacksToAdd(quantity)
    }
  }

  return (
    <Wrapper
      isLivBrite={isLivBrite}
      ref={customizationRef}
    >
      <CustomizationWrapper>
        <CustomizeWrapper
          isLivBrite={isLivBrite}
        >
          <h2>Customize your shipment.</h2>
          <label>How often do you want shipments?</label>
          <FlexWrapper
            verticalCenter
          >
            <SubscriptionFrequencyInput
              value={frequency}
              min={2}
              max={20}
              onChange={setFrequency}
            />
            <span>Weeks</span>
          </FlexWrapper>

          <ProductQtyInput
            qty={packs.value}
            handleQtyChange={handlePacksChange}
            packageType={packageType}
            packageQty={packageQty}
            price={productPrice}
            productId={productId}
            frequency={frequency}
            subtotalPrice={subtotalPrice}
            label='How many bags per shipment?'
          />
          <p className='note'>
            {itemsPerDay >= 1 && (
              `This should be enough for you to use 
                ${itemsPerDay} ${itemsPerDay > 1 ? 'pieces' : 'piece'} a day until your next shipment!`
            )}
            {itemsPerDay < 1 && (
              'This is enough for you to use less than one piece per day until your next shipment!'
            )}
          </p>
        </CustomizeWrapper>
        <SubscriptionWrapper>
          <h2>Your subscription.</h2>
          <CustomizationItem
            frequency={frequency}
            packageQty={packageQty}
            setFrequency={setFrequency}
            setPacksToAdd={setPacksToAdd}
            qtyPerDay={quantityUsedPerDay}
            packsToAdd={packsToAdd}
            finalPrice={subtotalPrice}
            loadingSubscription={loadingSubscription}
            isFreeShipping={Boolean(isFreeShipping)}
            packageType={packageType}
          />
          <p>
            Your subscription will start now. Change or cancel anytime.
          </p>
        </SubscriptionWrapper>
      </CustomizationWrapper>
    </Wrapper>
  )
}

const getItemsPerDay = ({ packs, packageQty, frequency }: { packs: number, packageQty: number, frequency: number }) => {
  const frequencyInDays = frequency * 7
  const itemsPerDay = packs * packageQty / frequencyInDays
  return Math.round(itemsPerDay * 2) / 2
}
